export type PublicShowActionInput = {
  publicId: string;
  name: string;
  description?: string | null;
  venueName: string;
  venueAddress: string;
  sessions: Array<{ startsAt: string; endsAt: string; }>;
};

export type ShowCalendarUrls = {
  google: string;
  outlook: string;
  ics: string;
};

const compactUtc = (value: string) =>
  new Date(value).toISOString().replaceAll("-", "").replaceAll(":", "").replace(/\.\d{3}Z$/, "Z");

const icsText = (value: string) =>
  value.replaceAll("\\", "\\\\").replaceAll("\n", "\\n").replaceAll(",", "\\,")
    .replaceAll(";", "\\;");

export function showCalendarUrls(show: PublicShowActionInput): ShowCalendarUrls | null {
  const sessions = [...show.sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  if (!sessions.length) return null;
  const startsAt = sessions[0].startsAt;
  const endsAt = sessions.reduce(
    (latest, session) => session.endsAt > latest ? session.endsAt : latest,
    sessions[0].endsAt,
  );
  const location = [show.venueName, show.venueAddress].filter(Boolean).join(", ");
  const description = show.description ?? "";
  const google = new URL("https://calendar.google.com/calendar/render");
  google.search = new URLSearchParams({
    action: "TEMPLATE",
    text: show.name,
    dates: `${compactUtc(startsAt)}/${compactUtc(endsAt)}`,
    details: description,
    location,
  }).toString();
  const outlook = new URL("https://outlook.live.com/calendar/0/deeplink/compose");
  outlook.search = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: show.name,
    startdt: new Date(startsAt).toISOString(),
    enddt: new Date(endsAt).toISOString(),
    body: description,
    location,
  }).toString();
  const events = sessions.map((session, index) =>
    [
      "BEGIN:VEVENT",
      `UID:${icsText(show.publicId)}-${index + 1}@trovie.io`,
      `DTSTAMP:${compactUtc(startsAt)}`,
      `DTSTART:${compactUtc(session.startsAt)}`,
      `DTEND:${compactUtc(session.endsAt)}`,
      `SUMMARY:${icsText(show.name)}`,
      `DESCRIPTION:${icsText(description)}`,
      `LOCATION:${icsText(location)}`,
      "END:VEVENT",
    ].join("\r\n")
  ).join("\r\n");
  const ics =
    `BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//Trovie//Show//EN\r\n${events}\r\nEND:VCALENDAR`;
  return {
    google: google.toString(),
    outlook: outlook.toString(),
    ics: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`,
  };
}

export function showMapUrl(
  show: Pick<PublicShowActionInput, "venueAddress" | "venueName">,
  appleMaps: boolean,
): string {
  if (appleMaps) {
    const url = new URL("https://maps.apple.com/");
    url.search = new URLSearchParams({ q: show.venueName, address: show.venueAddress }).toString();
    return url.toString();
  }
  const url = new URL("https://www.google.com/maps/search/");
  url.search = new URLSearchParams({
    api: "1",
    query: [show.venueName, show.venueAddress].filter(Boolean).join(", "),
  }).toString();
  return url.toString();
}
